import { customAlphabet } from "nanoid";
import type {
	DocumentGenerationVersion,
	DocumentHistoryVersion,
	DocumentTypedIdString,
	DocumentTypes,
	DocumentUniqueId,
} from "./types";
import {
	GENERATION_VERSION_SEPARATOR,
	HISTORY_VERSION_SEPARATOR,
	TYPE_SEPARATOR,
	idDocumentTypeStringMap,
} from "./validation";

export const customNanoId = customAlphabet(
	"0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
	22,
);

export interface DocumentId {
	type: DocumentTypes;
	id: DocumentUniqueId;
	generationVersion?: DocumentGenerationVersion;
	historyVersion?: DocumentHistoryVersion;
	isEqual(other: DocumentId): boolean;
	atGenerationVersion(generationVersion: DocumentGenerationVersion): DocumentId;
	atHistoryVersion(historyVersion: DocumentHistoryVersion): DocumentId;
	toString(): DocumentTypedIdString;
	toString(
		withGenerationVersion?: boolean,
		withHistoryVersion?: boolean,
	): DocumentTypedIdString;
	toIdString(): string;
}

export abstract class BaseDocumentId implements DocumentId {
	constructor(
		public readonly type: DocumentTypes,
		public id: DocumentUniqueId,
		public generationVersion?: DocumentGenerationVersion,
		public historyVersion?: DocumentHistoryVersion,
	) {}

	isEqual(other: DocumentId) {
		return (
			this.type === other.type &&
			this.id === other.id &&
			this.generationVersion === other.generationVersion &&
			this.historyVersion === other.historyVersion
		);
	}

	abstract atGenerationVersion(
		generationVersion: DocumentGenerationVersion,
	): DocumentId;
	abstract atHistoryVersion(historyVersion: DocumentHistoryVersion): DocumentId;

	toIdString(): string {
		if (this.generationVersion && this.historyVersion) {
			return `${this.id}${GENERATION_VERSION_SEPARATOR}${this.generationVersion}${HISTORY_VERSION_SEPARATOR}${this.historyVersion}`;
		}
		if (this.generationVersion) {
			return `${this.id}${GENERATION_VERSION_SEPARATOR}${this.generationVersion}`;
		}
		if (this.historyVersion) {
			return `${this.id}${HISTORY_VERSION_SEPARATOR}${this.historyVersion}`;
		}
		return this.id;
	}

	toString(
		withGenerationVersion = true,
		withHistoryVersion = true,
	): DocumentTypedIdString {
		const documentTypeString = idDocumentTypeStringMap[this.type];

		if (
			this.generationVersion &&
			withGenerationVersion &&
			this.historyVersion &&
			withHistoryVersion
		) {
			return `${documentTypeString}${TYPE_SEPARATOR}${this.id}${GENERATION_VERSION_SEPARATOR}${this.generationVersion}${HISTORY_VERSION_SEPARATOR}${this.historyVersion}`;
		}

		if (this.historyVersion && withHistoryVersion) {
			return `${documentTypeString}${TYPE_SEPARATOR}${this.id}${HISTORY_VERSION_SEPARATOR}${this.historyVersion}`;
		}

		if (this.generationVersion && withGenerationVersion) {
			return `${documentTypeString}${TYPE_SEPARATOR}${this.id}${GENERATION_VERSION_SEPARATOR}${this.generationVersion}`;
		}

		return `${documentTypeString}-${this.id}`;
	}
}
